import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import { news } from '@/mocks/news';
import { works } from '@/mocks/works';
import { searchAll, type SearchResult } from '@/utils/content';

const tabs: { key: 'all' | 'news' | 'work' | 'profile'; label: string }[] = [
  { key: 'all', label: '全部结果' },
  { key: 'news', label: '新闻资讯' },
  { key: 'work', label: '影视作品' },
  { key: 'profile', label: '明星资料' },
];

const kindMeta: Record<SearchResult['kind'], { label: string; style: string; icon: string }> = {
  news: { label: '新闻', style: 'bg-primary-100 text-primary-800', icon: 'ri-newspaper-line' },
  work: { label: '作品', style: 'bg-accent-100 text-accent-800', icon: 'ri-film-line' },
  profile: { label: '资料', style: 'bg-secondary-200 text-secondary-900', icon: 'ri-user-star-line' },
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<'all' | 'news' | 'work' | 'profile'>('all');

  useEffect(() => {
    setInput(query);
  }, [query]);

  const allResults = useMemo(() => searchAll(query), [query]);
  const results = useMemo(
    () => (tab === 'all' ? allResults : allResults.filter((r) => r.kind === tab)),
    [allResults, tab],
  );

  const counts = {
    all: allResults.length,
    news: allResults.filter((r) => r.kind === 'news').length,
    work: allResults.filter((r) => r.kind === 'work').length,
    profile: allResults.filter((r) => r.kind === 'profile').length,
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set('q', input.trim());
    setParams(next);
    setTab('all');
  };

  const hotWorks = works.slice(0, 4);
  const hotNews = news.slice(0, 3);

  return (
    <>
      <Seo
        title={query ? `搜索「${query}」｜深田咏美资讯` : '全站搜索｜深田咏美资讯'}
        description="在深田咏美资讯站内搜索新闻、作品与明星资料，快速定位你想要了解的内容。"
        keywords="深田咏美搜索,深田咏美资讯搜索,深田咏美作品搜索"
      />

      <section className="border-b border-background-200 bg-foreground-950">
        <div className="max-w-4xl mx-auto px-4 py-12 md:px-8 md:py-16">
          <div className="mb-6 [&_*]:text-background-300">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '全站搜索' }]} />
          </div>
          <h1 className="font-heading text-3xl font-black text-background-50 md:text-4xl">全站搜索</h1>
          <p className="mt-3 text-sm leading-relaxed text-background-300">
            输入关键词，搜索深田咏美的新闻动态、影视作品与明星资料。
          </p>
          <form onSubmit={submit} className="mt-7 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-lg text-secondary-400"></i>
              <input
                type="text"
                name="keyword"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：夜色微光、采访、纪录片"
                className="w-full rounded-md border border-background-300 bg-background-50 py-3.5 pl-12 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-4xl mx-auto px-4 py-10 md:px-8 md:py-14">
          {query ? (
            <>
              <p className="text-sm text-foreground-500">
                关键词 <strong className="text-foreground-900">「{query}」</strong> 共找到{' '}
                <strong className="text-primary-600">{counts.all}</strong> 条相关内容
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                      tab === t.key
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {t.label}
                    <span
                      className={`rounded-full px-1.5 text-xs ${
                        tab === t.key ? 'bg-background-50/25' : 'bg-background-300/60'
                      }`}
                    >
                      {counts[t.key]}
                    </span>
                  </button>
                ))}
              </div>

              {results.length > 0 ? (
                <div className="mt-8 flex flex-col divide-y divide-background-200 border-y border-background-200">
                  {results.map((r) => {
                    const meta = kindMeta[r.kind];
                    return (
                      <Link
                        key={`${r.kind}-${r.id}`}
                        to={r.path}
                        className="group flex gap-4 py-5 transition-colors hover:bg-background-100/60 cursor-pointer"
                      >
                        {r.image && (
                          <div className="hidden h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200 sm:block">
                            <img
                              src={r.image}
                              alt={`${r.title} 配图`}
                              title={`${r.title} 深田咏美`}
                              className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.style}`}
                          >
                            <i className={meta.icon}></i>
                            {meta.label}
                          </span>
                          <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                            {r.title}
                          </h3>
                          <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                            {r.desc}
                          </p>
                          <p className="mt-2 text-xs text-foreground-400">{r.meta}</p>
                        </div>
                        <i className="ri-arrow-right-line shrink-0 self-center text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-primary-600"></i>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/60 p-8 text-center">
                  <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-search-eye-line text-2xl"></i>
                  </span>
                  <p className="mt-4 font-heading text-lg font-bold text-foreground-900">
                    没有找到与「{query}」相关的内容
                  </p>
                  <p className="mt-1 text-sm text-foreground-500">
                    换个关键词试试，或从下面的热门内容开始浏览。
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 p-8 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-line text-2xl"></i>
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-900">开始你的搜索</p>
              <p className="mt-1 text-sm text-foreground-500">
                输入关键词，如「夜色微光」「采访」「纪录片」，即可搜索全站内容。
              </p>
            </div>
          )}

          <div className="mt-14">
            <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              热门内容推荐
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {hotWorks.map((w) => (
                <Link
                  key={w.id}
                  to={`/works/${w.id}`}
                  className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 hover:bg-background-100/60 cursor-pointer"
                >
                  <div className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={w.cover}
                      alt={`${w.title} 封面`}
                      title={`${w.title} 深田咏美作品`}
                      className="h-full w-full object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-700">
                      {w.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-400">
                      {w.type} · {w.year} 年
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              {hotNews.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-fire-line text-accent-500"></i>
                  {n.title.length > 16 ? `${n.title.slice(0, 16)}…` : n.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}